import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotsController } from '../../controllers/slots-ng-controller';
import { ResizeController } from '../../controllers/resize-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import '../sic-container/sic-container';

import componentStyles from './sic-simple-feature.css.lit';
import defaultVars from './css-variables/sic-simple-feature-vars.css.lit';

/**
 * The Simple Feature component.
 * @element sic-simple-feature
 *
 * @slot heading
 * @slot body
 * @slot cta
 * @slot cta2
 * @slot image
 *
 * @attr {boolean} media-first
 * @attr {boolean} bg-white - if not on white background, on mobile adds a
 * negtive margin to go to edge of container.
 *
 * Lightdom styles in sic-simple-feature.lightdom.css included in /shared.css
 */

@customElement('sic-simple-feature')
export class SicSimpleFeature extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, defaultVars];

  slots = new SlotsController(this);
  resizeController: ResizeController;

  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get('md') as number],
      containerQuery: false,
    });
  }

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'media-first',
  })
  mediaFirst: false;

  @property({
    type: Boolean,
    reflect: true,
    attribute: 'bg-white',
  })
  bgWhite: false;

  @state() private isStacked = false;

  render(): TemplateResult {
    this.isStacked = this.resizeController.currentBreakpointRange === 0;
    const wrapperClasses = {
      'simple-feature-wrapper': true,
      'layout-stacked': this.isStacked,
      'layout-media-first': this.mediaFirst,
      'layout-negative-magrin-mobile': !this.bgWhite,
    };

    return html`
      <sic-container
        container-width="full"
        top-padding="none"
        bottom-padding="none"
      >
        <div class="${classMap(wrapperClasses)}">${this.layoutTemplate()}</div>
      </sic-container>
    `;
  }

  layoutTemplate(): TemplateResult {
    if (this.isStacked || this.mediaFirst) {
      return html` ${this.mediaTemplate()} ${this.contentTemplate()} `;
    } else {
      return html` ${this.contentTemplate()} ${this.mediaTemplate()} `;
    }
  }

  contentTemplate(): TemplateResult {
    return html` <div class="content-layout">
      <div class="content">
        ${this.headingTemplate()} ${this.slots.conditionalSlot('body', false)}
        <div class="cta-wrapper">
          ${this.slots.conditionalSlot('cta', false)}
          ${this.slots.conditionalSlot('cta2', false)}
        </div>
      </div>
    </div>`;
  }
  mediaTemplate(): TemplateResult {
    return html`
      <div class="media-layout">
        <div class="image-container">
          ${this.slots.conditionalSlot('image', false)}
        </div>
      </div>
    `;
  }
  headingTemplate(): TemplateResult {
    if (this.slots.slotExists('heading')) {
      return html` <sic-heading level-size="6" class="content-heading">
        ${this.slots.conditionalSlot('heading')}
      </sic-heading>`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-simple-feature': SicSimpleFeature;
  }
}
